import { useEffect, useRef, useCallback, useState } from 'react';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import * as Sentry from '@sentry/react';

/**
 * Custom hook for real-time Asset Management updates via SignalR.
 * Manages connection to AssetManagementHub and handles live updates when
 * another user creates, updates, or deactivates an asset.
 *
 * @param {Object} callbacks - Event callbacks for different asset events
 * @param {Function} callbacks.onAssetChanged - Called when any asset change is received
 * @param {boolean} enabled - Whether the connection should be active
 * @returns {Object} - { isConnected, joinProperty, leaveProperty }
 */
const useAssetSignalR = (callbacks = {}, enabled = true) => {
    const connectionRef = useRef(null);
    const isConnectedRef = useRef(false);
    const reconnectTimeoutRef = useRef(null);
    const callbacksRef = useRef(callbacks);
    const joinedPropertyRef = useRef(null);
    const startConnectionRef = useRef(null);

    const [isConnected, setIsConnected] = useState(false);

    const setConnectionState = useCallback((connected) => {
        isConnectedRef.current = connected;
        setIsConnected(connected);
    }, []);

    useEffect(() => {
        callbacksRef.current = callbacks;
    }, [callbacks]);

    const isTokenExpired = useCallback((token) => {
        try {
            if (!token) return true;
            const base64Url = token.split('.')[1];
            const base64 = base64Url
                .replace(/-/g, '+')
                .replace(/_/g, '/')
                .padEnd(base64Url.length + (4 - (base64Url.length % 4)) % 4, '=');
            const payload = JSON.parse(atob(base64));
            return Date.now() >= payload.exp * 1000;
        } catch (catchError) {
            Sentry.captureException(catchError);
            return true;
        }
    }, []);

    const startConnection = useCallback(async () => {
        if (!enabled || isConnectedRef.current) return;

        try {
            const storedUserJson =
                localStorage.getItem('user') ||
                sessionStorage.getItem('user');
            const storedUser = storedUserJson ? JSON.parse(storedUserJson) : null;
            const token = storedUser?.token || null;

            if (!token) return;
            if (isTokenExpired(token)) return;

            const connection = new HubConnectionBuilder()
                .withUrl('/api/hubs/assetmanagement', {
                    accessTokenFactory: () => {
                        try {
                            const latestUserJson =
                                localStorage.getItem('user') ||
                                sessionStorage.getItem('user');
                            const latestUser = latestUserJson ? JSON.parse(latestUserJson) : null;
                            return latestUser?.token || token;
                        } catch (catchError) {
                            Sentry.captureException(catchError);
                            return token;
                        }
                    }
                })
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: (retryContext) => {
                        if (retryContext.previousRetryCount === 0) return 0;
                        if (retryContext.previousRetryCount === 1) return 2000;
                        if (retryContext.previousRetryCount === 2) return 10000;
                        if (retryContext.previousRetryCount === 3) return 30000;
                        return 60000;
                    }
                })
                .configureLogging(LogLevel.Information)
                .build();

            connection.on('AssetChanged', (data) => {
                callbacksRef.current.onAssetChanged?.(data);
            });

            connection.onreconnecting(() => {
                setConnectionState(false);
            });

            connection.onreconnected(() => {
                setConnectionState(true);
                if (joinedPropertyRef.current) {
                    connection.invoke('JoinPropertyGroup', parseInt(joinedPropertyRef.current)).catch(() => {});
                }
            });

            connection.onclose((error) => {
                setConnectionState(false);
                if (error && enabled) {
                    reconnectTimeoutRef.current = setTimeout(() => {
                        startConnectionRef.current?.();
                    }, 5000);
                }
            });

            await connection.start();
            connectionRef.current = connection;
            setConnectionState(true);
        } catch (catchError) {
            Sentry.captureException(catchError);
            reconnectTimeoutRef.current = setTimeout(() => {
                startConnectionRef.current?.();
            }, 5000);
        }
    }, [enabled, isTokenExpired, setConnectionState]);

    useEffect(() => {
        startConnectionRef.current = startConnection;
    }, [startConnection]);

    const joinProperty = useCallback(async (propertyId) => {
        if (!propertyId) return;
        // Leave previous property group if any
        if (joinedPropertyRef.current && connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('LeavePropertyGroup', parseInt(joinedPropertyRef.current));
            }
            catch (catchError) {
                Sentry.captureException(catchError);
                /* ignore */
            }
        }
        joinedPropertyRef.current = propertyId;
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('JoinPropertyGroup', parseInt(propertyId));
            }
            catch (catchError) {
                Sentry.captureException(catchError);
                /* ignore */
            }
        }
    }, []);

    const leaveProperty = useCallback(async (propertyId) => {
        if (!propertyId) return;
        if (joinedPropertyRef.current === propertyId) {
            joinedPropertyRef.current = null;
        }
        if (connectionRef.current && isConnectedRef.current) {
            try {
                await connectionRef.current.invoke('LeavePropertyGroup', parseInt(propertyId));
            }
            catch (catchError) {
                Sentry.captureException(catchError);
                /* ignore */
            }
        }
    }, []);

    useEffect(() => {
        if (enabled) {
            startConnectionRef.current?.();
        }

        return () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
            }
            if (connectionRef.current) {
                connectionRef.current.stop().catch(() => {});
                connectionRef.current = null;
                setConnectionState(false);
            }
        };
    }, [enabled, setConnectionState]);

    return { isConnected, joinProperty, leaveProperty };
};

export default useAssetSignalR;
